import { create } from 'zustand';
import { API_BASE_URL } from '@/lib/api';
import { setSessionHint, clearSessionHint } from '@/lib/session-hint';

interface AuthUser {
  id: string;
  fullName: string;
  displayName: string;
  profilePictureUrl: string | null;
  homeCity: string | null;
  isGuest?: boolean;
  messageViewStyle?: 'classic' | 'bubble';
}

export interface UserOrg {
  id: string;
  name: string;
  slug: string;
  color: string | null;
  icon: string | null;
  logoUrl?: string | null;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  orgs: UserOrg[];
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setOrgs: (orgs: UserOrg[]) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  hydrate: () => void;
}

const STORAGE_KEY = 'goodchat_auth';

function persistAuth(state: { accessToken: string | null; refreshToken: string | null; user: AuthUser | null }) {
  if (state.accessToken && state.refreshToken && state.user) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    );
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  orgs: [],
  isAuthenticated: false,
  isLoading: true,

  setAuth: (user, accessToken, refreshToken) => {
    const state = { user, accessToken, refreshToken, isAuthenticated: true, isLoading: false };
    persistAuth(state);
    // Lets goodchat.us send an already-signed-in visitor straight to the app.
    // Carries no credential — see lib/session-hint.ts.
    setSessionHint();
    set(state);
  },

  setOrgs: (orgs) => set({ orgs }),

  setTokens: (accessToken, refreshToken) => {
    const user = get().user;
    persistAuth({ accessToken, refreshToken, user });
    set({ accessToken, refreshToken });
  },

  setLoading: (isLoading) => set({ isLoading }),

  logout: () => {
    const { refreshToken } = get();
    // Fire-and-forget logout API call
    if (refreshToken) {
      fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    localStorage.removeItem(STORAGE_KEY);
    clearSessionHint();
    set({ user: null, accessToken: null, refreshToken: null, orgs: [], isAuthenticated: false, isLoading: false });
  },

  hydrate: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        setSessionHint();
        set({
          user: data.user,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({ isLoading: false });
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      set({ isLoading: false });
    }
  },
}));

// Multi-tab auth sync: the `storage` event fires in OTHER tabs when auth
// changes. Logout in one tab logs out all tabs immediately; token rotation
// in one tab keeps the others' in-memory tokens fresh (previously a rotation
// silently invalidated sibling tabs until refresh).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    if (e.newValue === null) {
      // Logged out elsewhere — clear local state only (no API call, no re-write)
      useAuthStore.setState({
        user: null,
        accessToken: null,
        refreshToken: null,
        orgs: [],
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }
    try {
      const data = JSON.parse(e.newValue);
      useAuthStore.setState({
        user: data.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      // Corrupt payload — ignore; this tab keeps its state
    }
  });
}
